import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import { PNG } from 'components/Asset/PNG';
import dayjs from 'dayjs';
import { type HealthScore } from 'types/squares.type';
import type { ServiceView } from 'types/survivalAnalysis.types';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { getPercentageDiff } from './utils';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import {
  type NotificationState,
  showNotification,
} from 'store/notification.slice';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import useAppTheme from 'hooks/useAppTheme';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const dateRangeList = ['1D', '7D', '14D', '1Y', 'Custom'];

const Header = ({
  selectedRange,
  setSelectedRange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleDateChange,
  selectedAsset,
  healthScores,
  handleBackClick,
}: {
  selectedRange: string;
  setSelectedRange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  handleDateChange: any;
  yesterday: any;
  setShowAssetDetails: any;
  setSelectedAsset: any;
  selectedAsset: ServiceView;
  healthScores: HealthScore[];
  handleBackClick: () => void;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const dispatch = useAppDispatch();
  const squaresHandler = new SquaresDashboardHandler();

  const { data, prevData, currDownTimeData } = useAppSelector(
    (state) => state.squaresPartsRecommendationsReducer
  );
  const fontColor = palette.textPrimary;

  // const assetHealthDetails = healthScores.find(
  //   (item) => item.asset_details.bumper_number === selectedAsset.item_id
  // );

  const pngName =
    selectedAsset?.oem && selectedAsset?.model && selectedAsset?.type
      ? getImageNameByMakeModelAndAsset(
          selectedAsset.oem,
          selectedAsset.model,
          selectedAsset.type,
          'Small'
        )
      : 'fallbackImageSmall';

  const handleRangeSelect = (id: string) => {
    setSelectedRange(id);
    let newStartDate = new Date();
    const newEndDate = new Date();

    switch (id) {
      case '1D':
        newStartDate = dayjs(newStartDate).subtract(1, 'day').toDate();
        break;
      case '7D':
        newStartDate = dayjs(newStartDate).subtract(7, 'days').toDate();
        break;
      case '14D':
        newStartDate = dayjs(newStartDate).subtract(14, 'days').toDate();
        break;
      case '1Y':
        newStartDate = dayjs(newStartDate).subtract(1, 'year').toDate();
        break;
      case 'Custom':
        break;
      default:
        break;
    }

    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      void squaresHandler.getPartsRecommendations(newStartDate, newEndDate);
    }
  };

  const workOrderData = useMemo(() => {
    return data.filter((item) => item.asset_id === selectedAsset.item_id);
  }, [data, selectedAsset.item_id]);

  const prevWorkOrderData = useMemo(() => {
    return prevData.filter((item) => item.asset_id === selectedAsset.item_id);
  }, [prevData, selectedAsset.item_id]);

  const totalWOCount = workOrderData.length;

  const totalWOCost = workOrderData.reduce((sum: number, item: any) => {
    return sum + Number(item.total_cost || 0);
  }, 0);

  const totalCurrentDownTimeHrs = useMemo(() => {
    if (!selectedAsset?.item_id || !currDownTimeData?.length) return 0;
    const match = currDownTimeData.find(
      (item) => item.asset_id === selectedAsset.item_id
    );
    const totalSeconds = (match?.pm_duration ?? 0) + (match?.cm_duration ?? 0);
    const totalHours = totalSeconds / 3600;
    return parseFloat(totalHours.toFixed(2));
  }, [selectedAsset?.item_id, currDownTimeData]);

  const prevTotalWOCount = prevWorkOrderData.length;

  const totalPrevWOCost = prevWorkOrderData.reduce((sum: number, item: any) => {
    return sum + Number(item.total_cost || 0);
  }, 0);

  const woCountDiff = getPercentageDiff(totalWOCount, prevTotalWOCount);

  const totalWOCostDiff = getPercentageDiff(totalWOCost, totalPrevWOCost);

  const handleApplyClick = () => {
    if (startDate && endDate) {
      void squaresHandler.getPartsRecommendations(startDate, endDate);
    } else {
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: `Invalid date selected, please select a valid range.`,
        show: true,
        type: 'error',
      };
      dispatch(showNotification(notificationPayload));
    }
  };

  return (
    <Box
      sx={{
        marginTop: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #26364C',
        padding: '5px 10px',
        borderRadius: '5px',
        backgroundColor: palette.paperBackground,
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#6B7280',
          fontSize: '14px',
        }}
      >
        <IconButton onClick={handleBackClick} aria-label="back">
          <ArrowCircleLeftIcon sx={{ color: palette.primary }} />
        </IconButton>

        <PNG
          name={pngName}
          alt={selectedAsset.item_id}
          style={{
            height: '62px',
            width: '80px',
          }}
        />
        <Box>
          <Typography
            sx={{ color: fontColor, fontSize: '14px', fontWeight: 700 }}
          >
            {selectedAsset.item_id}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: fontColor,
            }}
          >
            {selectedAsset.oem} - {selectedAsset.model}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              color: fontColor,
            }}
          >
            {selectedAsset?.type?.split('_')?.join(' ').toLocaleUpperCase()}
          </Typography>
        </Box>

        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          sx={{
            marginRight: '10px',
            marginLeft: '10px',
          }}
        >
          <Typography
            sx={{
              color: palette.grey,
              marginRight: '10px',
            }}
          >
            {t('quick_range_10')}
          </Typography>
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label={t('basic_button_group')}
            sx={{
              border: '1px solid #26364C',
            }}
          >
            {dateRangeList.map((item) => {
              return (
                <Button
                  key={item}
                  size="small"
                  style={{
                    backgroundColor:
                      selectedRange === item
                        ? palette.primary
                        : enableDarkTheme
                        ? palette.boxBackground
                        : palette.toolBarBackground,
                    color: palette.textPrimary,
                    border: 'none',
                  }}
                  onClick={() => {
                    handleRangeSelect(item);
                  }}
                >
                  {item}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>

        {selectedRange === 'Custom' ? (
          <Box display={'flex'} alignItems={'center'}>
            <Typography
              sx={{
                color: palette.grey,
              }}
            >
              {t('date_range_10')}
            </Typography>
            <div>
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                maxDate={dayjs(new Date().getTime())}
                wrap
                showDash={true}
                autoOpenEnabled={true}
                sx={{
                  width: '170px',
                  minWidth: '170px',
                }}
                onFinalDateChange={handleDateChange}
              />
            </div>

            <CustomLoadingButton
              onClick={handleApplyClick}
              sx={{
                fontSize: '12px',
                height: '30px',
              }}
            >
              {t('apply')}
            </CustomLoadingButton>
          </Box>
        ) : null}
      </Box>

      {/** Stats */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Health Score */}
        {/* <Box
          sx={{
            textAlign: 'center',
            borderRight: '1px solid #26364C',
            padding: '0px 20px',
          }}
        >
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '11px',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            Health Score
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                color: '#10B981',
                fontSize: '18px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <MonitorHeartIcon fontSize="inherit" />
              {truncateTo2dp(
                Number(assetHealthDetails?.asset_details.health_score) * 100
              )}
              %
            </Typography>
          </Box>
        </Box> */}

        {/* Work Orders */}
        <Box
          sx={{
            textAlign: 'center',
            borderRight: '1px solid #26364C',
            padding: '0px 20px',
          }}
        >
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '11px',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {t('work_orders')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{ color: palette.primary, fontSize: '18px', fontWeight: 600 }}
            >
              {totalWOCount}
            </Typography>

            <Tooltip
              title={`Difference: ${Math.abs(woCountDiff)}% ${
                woCountDiff > 0 ? 'More' : 'Less'
              } than prev duration`}
              arrow
              placement="top"
            >
              <span>
                {woCountDiff > 0 ? (
                  <CallMadeIcon
                    sx={{
                      color: '#EF4444',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  />
                ) : (
                  <CallReceivedIcon
                    sx={{
                      color: '#009161',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  />
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Maintenance Cost */}
        <Box
          sx={{
            textAlign: 'center',
            borderRight: '1px solid #26364C',
            padding: '0px 20px',
          }}
        >
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '11px',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {t('maintenance_cost')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{ color: '#EF4444', fontSize: '18px', fontWeight: 600 }}
            >
              ${formatNumberToShortScale(totalWOCost)}
            </Typography>
            <Tooltip
              title={`Difference: ${Math.abs(totalWOCostDiff)}% ${
                totalWOCostDiff > 0 ? 'More' : 'Less'
              } than prev duration`}
              arrow
              placement="top"
            >
              <span>
                {totalWOCostDiff > 0 ? (
                  <CallMadeIcon
                    sx={{
                      color: '#EF4444',
                      fontSize: '14px',
                    }}
                  />
                ) : (
                  <CallReceivedIcon
                    sx={{
                      color: '#009161',
                      fontSize: '14px',
                    }}
                  />
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Downtime */}
        <Box
          sx={{
            textAlign: 'center',
            padding: '0px 20px',
          }}
        >
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '11px',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {t('downtime')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{ color: '#F59E0B', fontSize: '18px', fontWeight: 600 }}
            >
              {totalCurrentDownTimeHrs}h
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
